import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useGetUsersQuery } from 'store/services/admin/adminApi';
import type { AdminUser } from 'store/services/admin/adminApi';
import './UserAutocomplete.css';

interface Props {
  value: string;
  onChange: (username: string) => void;
}

const DEBOUNCE_MS = 250;
const PAGE_SIZE = 8;

const UserAutocomplete: React.FC<Props> = ({ value, onChange }) => {
  const [input, setInput] = useState('');
  const [debounced, setDebounced] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Debounce the search input
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(input.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [input]);

  const shouldSearch = !value && debounced.length >= 2;

  const { data, isFetching } = useGetUsersQuery(
    { page: 1, pageSize: PAGE_SIZE, search: debounced },
    { skip: !shouldSearch }
  );

  const matches: AdminUser[] = useMemo(() => data?.users ?? [], [data]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (user: AdminUser) => {
    if (user.username) {
      onChange(user.username);
      setInput('');
      setDebounced('');
      setIsOpen(false);
    }
  };

  const handleClear = () => {
    onChange('');
    setInput('');
    setDebounced('');
    setIsOpen(true);
  };

  if (value) {
    return (
      <div className="user-autocomplete-chip">
        <span className="user-autocomplete-chip-label">@{value}</span>
        <button
          type="button"
          className="user-autocomplete-chip-clear"
          onClick={handleClear}
          aria-label="Clear selected user"
        >
          ×
        </button>
      </div>
    );
  }

  return (
    <div className="user-autocomplete" ref={wrapperRef}>
      <input
        type="text"
        className="broadcast-input"
        placeholder="Search by username or email..."
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        autoComplete="off"
      />
      {isOpen && shouldSearch && (
        <div className="user-autocomplete-dropdown">
          {isFetching ? (
            <div className="user-autocomplete-status">Loading…</div>
          ) : matches.length === 0 ? (
            <div className="user-autocomplete-status">No matches</div>
          ) : (
            matches.map((user) => (
              <button
                key={user.id}
                type="button"
                className="user-autocomplete-option"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(user)}
              >
                <span className="user-autocomplete-username">@{user.username}</span>
                <span className="user-autocomplete-email">{user.email}</span>
              </button>
            ))
          )}
        </div>
      )}
      {isOpen && !shouldSearch && input.length > 0 && input.length < 2 && (
        <div className="user-autocomplete-dropdown">
          <div className="user-autocomplete-status">Type 2+ characters to search</div>
        </div>
      )}
    </div>
  );
};

export default UserAutocomplete;
